import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { BreathingAura } from '../components/BreathingAura';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Trends = () => {
  const navigate = useNavigate();
  const [trends, setTrends] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    try {
      const [trendsData, checkinsData] = await Promise.all([
        api.getTrends(days),
        api.getCheckIns(days),
      ]);
      setTrends(trendsData);
      setCheckins(checkinsData);
    } catch (error) {
      toast.error('Failed to load trends');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card/90 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
          <p className="text-foreground font-medium">{formatDate(payload[0].payload.date)}</p>
          <p className="text-primary">
            Sentiment: {payload[0].value}/10
          </p>
          <p className="text-muted-foreground text-sm">
            {payload[0].payload.entry_count} {payload[0].payload.entry_count === 1 ? 'entry' : 'entries'}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-serif">Loading trends...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <BreathingAura variant="focus" />
      
      <div className="relative z-10">
        <div className="container mx-auto px-6 py-8 max-w-7xl">
          <Button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-full mb-8"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-5xl md:text-6xl font-serif font-light text-foreground tracking-tight mb-2">
                  Emotional Trends
                </h1>
                <p className="text-muted-foreground text-lg">Your journey over time</p>
              </div>
              
              <div className="flex gap-2">
                {[7, 14, 30].map((d) => (
                  <Button
                    key={d}
                    data-testid={`filter-${d}-days`}
                    onClick={() => setDays(d)}
                    className={`rounded-full px-6 py-2 ${
                      days === d
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card/50 text-muted-foreground hover:bg-card'
                    }`}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
            </div>

            <div className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8">
              <h2 className="text-2xl font-serif font-light text-foreground mb-6">Sentiment Over Time</h2>
              {trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={trends} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333230" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      stroke="#A1A09A"
                      tick={{ fill: '#A1A09A' }}
                    />
                    <YAxis
                      domain={[0, 10]}
                      stroke="#A1A09A"
                      tick={{ fill: '#A1A09A' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="sentiment_score"
                      stroke="#8DA399"
                      strokeWidth={3}
                      dot={{ fill: '#8DA399', r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-16 text-muted-foreground">
                  No data available for this period. Start checking in to see your trends!
                </div>
              )}
            </div>

            <div className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8">
              <h2 className="text-2xl font-serif font-light text-foreground mb-6">Recent Check-ins</h2>
              {checkins.length > 0 ? (
                <div className="space-y-4">
                  {checkins.map((checkin) => (
                    <div
                      key={checkin.id}
                      data-testid={`checkin-${checkin.id}`}
                      className="bg-muted/30 rounded-2xl p-6 border border-white/5 hover:border-white/10 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="text-sm text-muted-foreground">
                          {new Date(checkin.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-primary font-medium">{checkin.sentiment_score}/10</span>
                          <span className="text-sm text-muted-foreground">•</span>
                          <span className="text-sm text-foreground">{checkin.sentiment_label}</span>
                        </div>
                      </div>
                      <p className="text-foreground leading-relaxed mb-4">{checkin.text}</p>
                      {checkin.stress_triggers?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {checkin.stress_triggers.map((trigger, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-destructive/20 text-destructive rounded-full text-xs"
                            >
                              {trigger}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-muted-foreground">
                  No check-ins yet. Start your emotional journey today!
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Trends;